import { useState } from 'react';
import {
  Actions,
  CondParams,
  Dispatchers,
  EmptyObject,
  F1,
  FDispatch,
  Parametrize,
  SMap,
} from './types';

export const extendDispatch = <T2 extends EmptyObject>(d: F1<Actions<T2>>) => {
  const dispatch = <T extends keyof T2>(type: T, payload: T2[T]) =>
    d({ type, payload } as Actions<T2>);
  const lazyDispatch =
    <T extends keyof T2>(type: T, payload: T2[T]) =>
    () =>
      dispatch(type, payload);
  return { dispatch, lazyDispatch };
};

export const getDispatchers = <T2 extends Record<string, any>>(
  orgDispatch: (v: Actions<T2>) => void,
): Dispatchers<T2> => {
  return {
    dispatch: <T extends keyof T2>(type: T, ...args: Parametrize<T2[T]>) => {
      const payload = args.length > 0 ? args[0] : (null as T2[T]);
      orgDispatch({ type, payload } as Actions<T2>);
    },

    lazyDispatch:
      <T extends keyof T2>(type: T) =>
      (...args: Parametrize<T2[T]>) => {
        const payload = args.length > 0 ? args[0] : (null as T2[T]);
        orgDispatch({ type, payload } as Actions<T2>);
      },
  };
};

type ActionHandlersMap<
  TPayloads extends Record<string, unknown>,
  TResultMap extends { [TAction in keyof TPayloads]: unknown },
> = {
  [TAction in keyof TPayloads]: (
    ...args: CondParams<TPayloads, TAction>
  ) => TResultMap[TAction];
};

export const createDispatch = <
  TPayloads extends Record<string, unknown>,
  TResultMap extends { [TAction in keyof TPayloads]: unknown },
>(
  handlers: ActionHandlersMap<TPayloads, TResultMap>,
): FDispatch<TPayloads, TResultMap> => {
  return <TAction extends keyof TPayloads>(
    type: TAction,
    ...args: CondParams<TPayloads, TAction>
  ): TResultMap[TAction] => {
    return handlers[type](...args);
  };
};

export const toMap = <T extends { [P in TK]: string }, TK extends keyof T, TV>(
  rs: T[],
  key: TK,
  toValue?: F1<T, TV>,
): SMap<TV> =>
  rs.reduce(
    (acc, r) => ({ ...acc, [r[key]]: toValue ? toValue(r) : (r as any) }),
    {},
  );

export const clone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(clone) as any;
  return Object.entries(obj).reduce(
    (acc, [k, v]) => ({ ...acc, [k]: clone(v) }),
    {} as T,
  );
};

export const values = <T>(obj: SMap<T>) => Object.values(obj) as T[];

const getEmptyMap = <T>() => ({} as SMap<T>);
export const useMap = <T>() => useState(getEmptyMap<T>());
